/**
 * /university/ab-tests/$testId — single experiment detail + conclude UI.
 *
 * Surface:
 *   - Variant A (control) vs Variant B (candidate) side-by-side.
 *   - Running counts + composite scores per variant.
 *   - Welch's t-test summary (lift, p-value, decision).
 *   - Conclude buttons (promote / reject) — gated to writer roles.
 */
import type { AbTestEvaluation } from '@chaos/types/learning';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  CheckCircle2,
  ChevronLeft,
  Loader2,
  ThumbsDown,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import type * as React from 'react';

import { Button } from '../../../../components/ui/button';
import { useToaster } from '../../../../components/ui/toast';
import { fmtCount, fmtRelative, fmtScore } from '../../../../components/university/formatters';
import {
  FINAL_AB_STATUSES,
  inferAbWinner,
} from '../../../../components/university/learning-helpers';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

const FINAL_STATUSES = FINAL_AB_STATUSES;

function AbTestDetailRoute() {
  const { testId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToaster();

  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canDecide = role === 'founder' || role === 'admin' || role === 'tuner';

  const detailQ = useQuery({
    queryKey: learningKeys.abTest(testId),
    queryFn: () => learningApi.getAbTest(testId),
    staleTime: 20_000,
    refetchInterval: 20_000,
  });

  const concludeM = useMutation({
    mutationFn: (decision: 'promote' | 'reject') =>
      learningApi.concludeAbTest(testId, { decision }),
    onSuccess: (_data, decision) => {
      qc.invalidateQueries({ queryKey: learningKeys.abTests });
      qc.invalidateQueries({ queryKey: learningKeys.abTest(testId) });
      toast({
        title: decision === 'promote' ? 'Candidate promoted' : 'Candidate rejected',
      });
      void navigate({ to: '/university/ab-tests' });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Conclude failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  if (detailQ.isPending) {
    return (
      <section className="mx-auto flex w-full max-w-4xl items-center justify-center gap-2 p-12 text-[12px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading experiment…
      </section>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <section className="mx-auto flex w-full max-w-4xl flex-col gap-3 p-6">
        <Link
          to="/university/ab-tests"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to A/B tests
        </Link>
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Experiment not found.
        </div>
      </section>
    );
  }

  const { test, evaluation } = detailQ.data;
  const isFinal = FINAL_STATUSES.has(test.status);
  const winner = inferAbWinner(evaluation, isFinal);

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
      <Link
        to="/university/ab-tests"
        className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3 w-3" aria-hidden /> Back to A/B tests
      </Link>

      <header className="flex flex-col gap-2 rounded border border-border bg-background p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <h1 className="text-[18px] font-semibold tracking-tight text-foreground">
              {test.skillName ?? `A/B test ${test.id.slice(0, 8)}`}
            </h1>
            <span className="mt-0.5 font-mono text-[11px] text-muted-foreground">
              control v{test.controlVersion} → candidate v{test.candidateVersion} · split{' '}
              {(test.trafficSplit * 100).toFixed(0)}% candidate
            </span>
          </div>
          <span className="rounded border border-border bg-muted/40 px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            {test.status}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span>started {fmtRelative(test.startedAt)}</span>
          {test.endedAt ? (
            <span>ended {fmtRelative(test.endedAt)}</span>
          ) : (
            <span>scheduled end {fmtRelative(test.endsAt)}</span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <VariantCard
          side="A"
          label="Control"
          version={test.controlVersion}
          n={evaluation?.controlN ?? null}
          score={evaluation?.controlScore ?? null}
          isWinner={winner === 'A'}
          isLoser={winner === 'B' && isFinal}
        />
        <VariantCard
          side="B"
          label="Candidate"
          version={test.candidateVersion}
          n={evaluation?.candidateN ?? null}
          score={evaluation?.candidateScore ?? null}
          isWinner={winner === 'B'}
          isLoser={winner === 'A' && isFinal}
          previewText={test.candidatePromptText}
        />
      </div>

      <StatisticalSummary evaluation={evaluation} />

      {canDecide && !isFinal ? (
        <footer className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={concludeM.isPending}
            onClick={() => concludeM.mutate('reject')}
          >
            <ThumbsDown className="h-3.5 w-3.5" aria-hidden /> Reject candidate
          </Button>
          <Button
            type="button"
            disabled={concludeM.isPending}
            onClick={() => concludeM.mutate('promote')}
          >
            {concludeM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            )}
            Promote candidate
          </Button>
        </footer>
      ) : null}
    </section>
  );
}

interface VariantCardProps {
  side: 'A' | 'B';
  label: string;
  version: string;
  n: number | null;
  score: number | null;
  isWinner: boolean;
  isLoser: boolean;
  previewText?: string | null;
}

function VariantCard({
  side,
  label,
  version,
  n,
  score,
  isWinner,
  isLoser,
  previewText,
}: VariantCardProps) {
  return (
    <article
      className={cn(
        'flex flex-col gap-2 rounded border bg-background p-4 transition-colors',
        isWinner && 'border-success/60 ring-1 ring-success/20',
        isLoser && 'border-destructive/40 ring-1 ring-destructive/15',
        !isWinner && !isLoser && 'border-border',
      )}
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] tabular-nums">
            {side}
          </span>
          <span className="text-[13px] font-semibold text-foreground">{label}</span>
        </div>
        {isWinner ? (
          <span className="inline-flex items-center gap-1 rounded bg-success/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-success">
            <CheckCircle2 className="h-3 w-3" aria-hidden /> winner
          </span>
        ) : isLoser ? (
          <span className="inline-flex items-center gap-1 rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-destructive">
            <XCircle className="h-3 w-3" aria-hidden /> loser
          </span>
        ) : null}
      </header>
      <div className="font-mono text-[11px] text-muted-foreground">v{version}</div>
      <div className="flex items-baseline gap-3">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">runs</span>
          <span className="font-mono text-2xl font-semibold tabular-nums text-foreground">
            {n != null ? fmtCount(n) : '—'}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">score</span>
          <span className="font-mono text-2xl font-semibold tabular-nums text-foreground">
            {score != null ? fmtScore(score) : '—'}
          </span>
        </div>
      </div>
      {previewText ? (
        <details className="mt-1 rounded border border-border bg-muted/30 p-2">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-muted-foreground">
            Candidate prompt
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-foreground/90">
            {previewText}
          </pre>
        </details>
      ) : null}
    </article>
  );
}

function StatisticalSummary({ evaluation }: { evaluation: AbTestEvaluation | null }) {
  if (!evaluation) {
    return (
      <div className="rounded border border-border bg-muted/20 p-3 text-[12px] text-muted-foreground">
        No evaluation yet. Wait for more attempts on both variants.
      </div>
    );
  }
  const lift = evaluation.relativeLift;
  const direction = lift == null ? 'flat' : lift > 0 ? 'up' : 'down';
  const liftColor =
    direction === 'flat'
      ? 'text-muted-foreground'
      : direction === 'up'
        ? 'text-success'
        : 'text-destructive';
  return (
    <div className="flex flex-col gap-2 rounded border border-border bg-background p-4 text-[12px]">
      <header className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-foreground">Welch's t-test</h3>
        <span
          className={cn(
            'rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider',
            evaluation.significant ? 'border-success/60 text-success' : 'text-muted-foreground',
          )}
        >
          {evaluation.significant ? 'significant' : 'not significant'}
        </span>
      </header>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="lift">
          <span className={cn('inline-flex items-center gap-1 font-mono tabular-nums', liftColor)}>
            {direction === 'up' ? (
              <TrendingUp className="h-3 w-3" aria-hidden />
            ) : direction === 'down' ? (
              <TrendingDown className="h-3 w-3" aria-hidden />
            ) : null}
            {lift != null ? `${(lift * 100).toFixed(1)}%` : '—'}
          </span>
        </Stat>
        <Stat label="p-value">
          <span className="font-mono tabular-nums text-foreground">
            {evaluation.pValue != null ? evaluation.pValue.toFixed(3) : '—'}
          </span>
        </Stat>
        <Stat label="control N">
          <span className="font-mono tabular-nums text-foreground">
            {fmtCount(evaluation.controlN)}
          </span>
        </Stat>
        <Stat label="candidate N">
          <span className="font-mono tabular-nums text-foreground">
            {fmtCount(evaluation.candidateN)}
          </span>
        </Stat>
      </div>
      <div className="rounded bg-muted/30 p-2 text-[11px] text-muted-foreground">
        Decision: <span className="font-medium text-foreground">{evaluation.decision}</span>
      </div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-[14px]">{children}</span>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/university/ab-tests/$testId')({
  component: AbTestDetailRoute,
});
